// @core/stores/project/ProjectMilestoneStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { projectApi } from '@core/api';
import type {
  CreateMilestoneRequest,
  Milestone,
  UpdateMilestoneRequest,
} from '@core/types/project';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProjectMilestoneStore {
  // List state
  items: Milestone[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Milestone | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Milestone>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Milestone | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Milestone | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create milestone
   */
  async CreateMilestone(project_id: string, data: CreateMilestoneRequest): Promise<Milestone> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.CreateMilestone(project_id, data);
      const item = response.milestone;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete milestone
   */
  async DeleteMilestone(project_id: string, milestone_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await projectApi.DeleteMilestone(project_id, milestone_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== milestone_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === milestone_id) {
          this.currentItem = null;
        }

        this.removeFromCache(milestone_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * List milestones
   */
  async ListMilestones(project_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await projectApi.ListMilestones(project_id, params);

      runInAction(() => {
        this.items = response.milestones?.milestones ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update milestone
   */
  async UpdateMilestone(project_id: string, milestone_id: string, data: UpdateMilestoneRequest): Promise<Milestone> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.UpdateMilestone(project_id, milestone_id, data);
      const item = response.milestone;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const projectMilestoneStore = new ProjectMilestoneStore();
